import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { tokens, IconButton, Text } from '@hotjar/hotsauce';

const Link = styled.a`
  display: flex;
  overflow: hidden;
  position: relative;
  flex-direction: column;
  gap: ${tokens.spacer.x0};
  ${tokens.transition('background-color')};
  padding: ${tokens.spacer.x1pt5};
  border-radius: ${tokens.radius.l};
  border: 1px solid ${tokens.color.ui_border_light};
  background-color: ${tokens.BRAND_COLORS.cobalt_100};

  &:before {
    content: '';
    display: block;
    position: absolute;
    width: 160px;
    height: 160px;
    right: -130px;
    top: -100px;
    background-image: radial-gradient(
      105% 117.79% at 48.77% 80.34%,
      rgba(161, 143, 214, 0.3) 8.43%,
      rgba(195, 240, 247, 0.3) 58.85%,
      rgba(195, 240, 247, 0) 100%
    );
    mix-blend-mode: darken;
    filter: blur(40px);
  }

  &:hover,
  &:focus {
    text-decoration: none;
    background-color: ${tokens.BRAND_COLORS.cobalt_200};
  }
`;

const Wrapper = styled.article`
  width: 190px;
  min-width: 190px;
  overflow: hidden;
  position: relative;

  &:nth-child(3n + 2) ${Link}:before {
    top: auto;
    bottom: -150px;
    transform: rotate(120deg);
  }

  &:nth-child(3n + 3) ${Link}:before {
    right: auto;
    left: -90px;
    transform: rotate(-120deg);
  }
`;

const Banner = styled.span`
  height: 100px;
  width: 100%;
  background-size: contain;
  background-position: center center;
  background-repeat: no-repeat;
`;

const DismissButton = styled(IconButton)`
  position: absolute;
  top: 0;
  right: 0;
`;

export interface TileProps {
  id: string;
  href: string;
  image: string;
  title: string;
  description: string;
  isExternalLink?: boolean;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onDismiss: () => void;
  onView: (id: string) => void;
  className?: string;
}

export const Tile = ({
  id,
  href,
  image,
  title,
  description,
  isExternalLink,
  onClick,
  onDismiss,
  onView,
  className,
}: TileProps): JSX.Element => {
  const [tileViewed, setIsTileViewed] = useState(false);

  const handleOnDismiss = () => {
    onDismiss();
  };

  useEffect(() => {
    if (!tileViewed) {
      onView(id);
      setIsTileViewed(true);
    }
  }, [id, onView, tileViewed]);

  return (
    <Wrapper className={className}>
      <Link
        href={href}
        onClick={onClick}
        aria-label={title}
        aria-details={description}
        target={isExternalLink ?? false ? '_blank' : '_self'}
      >
        <Banner css={{ backgroundImage: `url(${image})` }} aria-hidden />
        <Text as="span" textType="subtitle2">
          {title}
        </Text>
        <Text as="span" textType="caption" textColor="text_light">
          {description}
        </Text>
      </Link>
      <DismissButton
        icon="close"
        kind="ghost"
        tooltipText="Dismiss"
        description="Dismiss"
        onClick={handleOnDismiss}
      />
    </Wrapper>
  );
};
